import { studio, disclaimers } from "@/data/site";

// ─────────────────────────────────────────────────────────────
// APP REGISTRY — add a new object here and the whole site
// (home, /apps, /apps/:slug, support selector, footer, SEO)
// updates automatically. No template changes needed.
//
// status: "published" | "coming-soon" | "draft"
//   published   → live everywhere, store buttons active
//   coming-soon → listed, marked Coming Soon, buttons disabled
//   draft       → hidden from the site
// ─────────────────────────────────────────────────────────────

export const apps = [
  {
    id: "aurahealth",
    slug: "aurahealth",
    name: "AuraHealth",
    category: "Health & Education",
    status: "published",
    featured: true,
    releaseDate: "2026",
    accent: "#E8A0A8",
    icon: "https://static.prod-images.emergentagent.com/jobs/7e87f1c8-a249-47d0-b763-82c8a3af4fe2/images/e73714ebeb2f1eebaed04f1d8d9ca5a98465a4d997c6df7b32ea61c325c5dfb6.jpeg",
    tagline: "Understand your cycle, in your own words.",
    shortDescription:
      "An education-first companion for cycle tracking, symptom and habit logging, and learning how hormones shape your day — with Luna, your in-app guide.",
    fullDescription:
      "AuraHealth helps you keep a simple, private record of your cycle, symptoms and daily habits, then explains what is generally happening in the body across each phase in plain language. Log your period and symptoms in seconds, keep a gentle daily streak across meals, supplements and movement, read short lessons on hormones and PMOS, and ask Luna — the in-app companion — anything you are unsure about. When you next see your doctor, you can export a Doctor Report PDF of your own data. AuraHealth is educational only: it does not diagnose or treat anything, and it is not a substitute for a healthcare professional.",
    audience: [
      "Anyone who wants a clearer picture of their own cycle and symptoms",
      "People learning about PMOS, hormones and insulin resistance",
      "Anyone building steady daily habits around food, supplements and movement",
      "Anyone who wants their own data in one place before a gynaecologist appointment",
    ],
    features: [
      { title: "Cycle & period logging", body: "Pick a start date on the calendar and log symptoms like cramps, fatigue, bloating, mood changes and more." },
      { title: "Luna, your companion", body: "Ask questions by voice or chat. Luna explains the educational content and nudges you about habits you have not logged." },
      { title: "PMOS education & risk chat", body: "A friendly, educational conversation to help you reflect on symptoms and decide whether to raise them with a doctor. Not a diagnosis." },
      { title: "Health habits & streak", body: "Track meals, supplements, workouts, water and sleep. Any two of three keeps your streak — a workout is always a bonus." },
      { title: "Education library", body: "Short lessons across Basics, Health, Fertility and Lifestyle, from 'What is PMOS?' to hormone imbalance and insulin resistance." },
      { title: "Supplement reference", body: "Plain-language reference on commonly discussed supplements — inositol, vitamin D, magnesium, omega-3, zinc, NAC and more." },
      { title: "Progress & trends", body: "Daily health score, 7-day activity, average water and cycle length, all built from what you logged." },
      { title: "Doctor Report PDF", body: "Export a clean summary of your own logs to save or share with your gynaecologist." },
      { title: "Sign in your way", body: "Continue with Apple, Google or email. The free plan starts right after sign-in — no credit card needed." },
      { title: "Private by default", body: "We ask for the minimum data needed and never sell personal information." },
    ],
    pricing: {
      free: [
        "Cycle and period logging",
        "Daily symptom & mood log",
        "Health habits tracker and streak",
        "Basic phase explanations",
      ],
      premium: [
        "Full education library",
        "Extended progress & trend views",
        "Doctor Report PDF export",
        "Expanded supplement reference",
      ],
      note: "Premium is optional. The core log and habit tracker are free and stay free.",
    },
    platforms: ["iOS", "Android"],
    appStoreUrl: "", // ← paste your real App Store URL here
    googlePlayUrl: "", // ← paste your real Google Play URL here
    screenshots: [
      { url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_app-ecosystem-base/artifacts/vzm5eh6m_Screenshot_20260907_011259_AuraHealth.webp", caption: "Home — streak, Luna and next period" },
      { url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_app-ecosystem-base/artifacts/6fp7sqyd_Screenshot_20260907_011330_AuraHealth.jpg", caption: "Log a period and its symptoms" },
      { url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_app-ecosystem-base/artifacts/1kvxftdl_Screenshot_20260907_011352_AuraHealth.webp", caption: "Health habits and daily score" },
      { url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_app-ecosystem-base/artifacts/siq6ygkc_Screenshot_20260907_011527_AuraHealth.webp", caption: "Education library by category" },
      { url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_app-ecosystem-base/artifacts/s735k9sx_Screenshot_20260907_011459_AuraHealth.webp", caption: "Supplement reference" },
      { url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_app-ecosystem-base/artifacts/vd8q72dd_Screenshot_20260907_011405_AuraHealth.webp", caption: "Progress and Doctor Report export" },
      { url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_app-ecosystem-base/artifacts/im7xcd61_Screenshot%202026-09-09%20at%2001.36.42.png", caption: "Sign in with Apple, Google or email" },
    ],
    isHealthApp: true,
    disclaimer: disclaimers.health,
    supportEmail: "aurahealth@tutinsapp.live",
    supportUrl: "/support",
    privacyUrl: "/privacy",
    termsUrl: "/terms",
    sourcesUrl: "/sources",
    seo: {
      title: "AuraHealth — cycle & hormone education app",
      description:
        "AuraHealth is an education-first cycle, symptom and habit tracking app by tutinsapp, with a PMOS education library and Luna, your in-app companion. Educational information only — not medical advice.",
    },
  },
  {
    id: "next-app",
    slug: "next-app",
    name: "New app coming soon",
    category: "In development",
    status: "coming-soon",
    featured: false,
    releaseDate: "To be announced",
    accent: "#E07A5F",
    icon: "https://static.prod-images.emergentagent.com/jobs/7e87f1c8-a249-47d0-b763-82c8a3af4fe2/images/b500c8e397067723b11eccf61565d35b64955f86ae16e46f32671f0714b4312f.jpeg",
    tagline: "Something new is in the studio.",
    shortDescription:
      "Our next app is in development. Details, screenshots and store links will appear here once it is ready.",
    fullDescription:
      "We are working on our next app. When it is ready, this page will hold its name, description, screenshots, features and download links — added by editing one entry in the app registry, with no redesign needed. Until then there is nothing to announce and no release date to promise.",
    audience: [
      "To be announced once the app is defined",
    ],
    features: [
      { title: "In development", body: "Features will be listed here once they are built and tested." },
      { title: "No release date yet", body: "We will announce a date only when we are confident in it." },
      { title: "Same studio principles", body: "Education before advice, minimal data collection, support answered by the people who build it." },
    ],
    pricing: { free: ["To be announced"], premium: ["To be announced"], note: "Pricing has not been decided yet." },
    platforms: ["iOS", "Android"],
    appStoreUrl: "",
    googlePlayUrl: "",
    screenshots: [],
    isHealthApp: false,
    disclaimer: disclaimers.general,
    supportEmail: "", // published once the app launches — until then the support form handles requests
    supportUrl: "/support",
    privacyUrl: "/privacy",
    termsUrl: "/terms",
    sourcesUrl: "",
    seo: {
      title: "New app coming soon",
      description: "A new app from tutinsapp studio is in development. No release date announced yet.",
    },
  },
];

export const visibleApps = apps.filter((a) => a.status !== "draft");
export const publishedApps = apps.filter((a) => a.status === "published");
export const featuredApp = apps.find((a) => a.featured && a.status === "published");
export const getApp = (slug) => visibleApps.find((a) => a.slug === slug);
